/* eslint-disable no-use-before-define */

import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useAtom, useAtomValue } from 'jotai';
import { NestedSearch, NestedResult as NestedResultType, Data, SearchResult } from '@shared/zod-schemas';
import { useRouter } from '@/i18n/routing';
import { resetSearchAtom, searchValueAtom } from '@/components/command/store';
import { highlightText } from '@/components/command/utils/highlightText';

export function NestedCategory({
	ns,
	onClose,
	indentLevel = 1,
}: {
	ns: NestedSearch;
	onClose: () => void;
	indentLevel?: number;
}) {
	const t = useTranslations('Command');

	return (
		<Box pl={4 * indentLevel} bg="bg.card" pt={2} pb={1}>
			<Text fontSize="12px" textTransform="uppercase" letterSpacing="wide" mb={1} color="fg.muted">
				{t(`${ns.category}.${ns.category}`)}
			</Text>
			<Flex flexDir="column" gap={1}>
				{ns.results.map((nr: NestedResultType) => (
					<NestedResult
						key={nr.id}
						nr={nr}
						categoryName={ns.category}
						onClose={onClose}
						indentLevel={indentLevel}
					/>
				))}
			</Flex>
		</Box>
	);
}

export function NestedResult({
	nr,
	categoryName,
	onClose,
	indentLevel = 1,
}: {
	nr: SearchResult;
	categoryName: string;
	onClose: () => void;
	indentLevel?: number;
}) {
	const router = useRouter();
	const t = useTranslations('Command');
	const [, resetSearch] = useAtom(resetSearchAtom);
	const searchValue = useAtomValue(searchValueAtom);

	return (
		<>
			<Box
				onClick={() => {
					resetSearch();
					onClose();
					router.push(nr.href);
				}}
				cursor="pointer"
				pl={4 * indentLevel}
				py={2}
				px={4}
				rounded="8px"
				transition="all 0.2s ease"
				_hover={{ bg: 'bg.neutralAccentAlpha' }}
			>
				<Text fontSize="14px" fontWeight="semibold">
					{highlightText(nr.data.find((el: Data) => el.isTitle)?.value ?? '', searchValue)}
				</Text>
				{nr.data
					.filter((el: Data) => !el.isTitle && el.value)
					.map((el: Data) => (
						<Text key={el.key} fontSize="12px">
							{t(`${categoryName}.${el.key}`)}: {highlightText(el.value!, searchValue)}
						</Text>
					))}
			</Box>
			{nr.nestedSearch?.map((child: NestedSearch) => (
				<NestedCategory key={child.id} ns={child} onClose={onClose} indentLevel={indentLevel + 1} />
			))}
		</>
	);
}
